import {useState} from 'react'
import i18n from "../../i18n";
import {useCheckout, PaymentElement} from "@stripe/react-stripe-js";
import {formatMoney, getReserveReturnUrl, isCompanyValid, validateEmail} from "../../utils/Toolbox";
import {Layout, StripePaymentElementOptions} from "@stripe/stripe-js";
import {Alert, Button, Card, Col, Container, Form, Row, Spinner} from "react-bootstrap";
import {PhoneInput} from "react-international-phone";
import {PhoneNumberUtil} from "google-libphonenumber";
import { Trans } from 'react-i18next';
import {getUrlLangPrefix} from "../../utils/Local";
import apiService from "../../services/api";

const paymentElementOptions = {
    layout: "tabs" as Layout,
} as StripePaymentElementOptions

type Contact = {
    name: string;
    email: string;
    phone: string;
    company: string;
};

const ReserveCheckoutFormComponent: React.FC = () => {

    const {t} = i18n;
    const checkout = useCheckout();
    const langPrefix = getUrlLangPrefix()

    const [contact, setContact] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
    } as Contact);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const phoneUtil = PhoneNumberUtil.getInstance();

    const isPhoneValid = () => {

        try {
            return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(contact.phone));
        } catch (error) {
            return false;
        }

    };

    const isReserveButtonDisabled = () => {
        return isLoading || !isPhoneValid() || !validateEmail(contact.email)
    }

    const handleUpdateContact = async () => {
        if (isReserveButtonDisabled()) {
            return;
        }

        if (!validateEmail(contact.email)) {
            return
        }

        if (contact.phone && !isPhoneValid()) {
            return
        }

        if (contact.company && !isCompanyValid(contact.company)) {
            return
        }
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        setIsLoading(true);

        try {
            await apiService.reserve(checkout.id, contact);
        } catch (error) {
            console.log(error)
            setMessage(t('_error_message'));
            return;
        }
        const updateEmailResult = await checkout.updateEmail(contact.email);
        if (updateEmailResult.type === 'error') {
            setMessage(updateEmailResult.error.message);
            return;
        }

        const checkoutResult = await checkout.confirm({
            returnUrl: getReserveReturnUrl(),
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (checkoutResult.type === 'error') {
            setMessage(checkoutResult.error.message);
        }

        setIsLoading(false);
    };

    const models = [
        {
            name: 'PakAlps',
            price: 78000,
            size: {
                label: t('One or two bedroom house'),
                value: t('392 sq ft to 522 sq ft'),
            },
            lifeTime: 400,
            img: '/images/models/pak-alps/PakAlps_exterior05.jpg',
        },
        {
            name: 'PakMonaco',
            price: 198000,
            size: {
                label: t('Two or three bedroom house'),
                value: t('1000 sq ft to 1500 sq ft'),
            },
            lifeTime: 400,
            img: '/images/models/pak-miami/PakMiami-exterior01.jpg',
        },
    ];

    return (<>
        <Container>
            <Row>
                <Col xs={12}>
                    <h2>{t('Reserve Now')}</h2>
                    <p>{t('Reserve your place in line for $150.00 and join the innovators embracing PakVille’s sustainable, long-lasting homes. Your reservation fee is fully refundable until you confirm your order. Model selection comes later—no pressure.')}</p>
                </Col>
                <Col xs={12}>
                    <Row>
                        {models.map((model, index) => {
                            return (
                                <Col xs={12} md={6} key={index}>
                                    <Card className="mb-3">
                                        <Card.Body>
                                            <Card.Title><h2
                                                className="modelHeader">{t(model.name)}</h2>
                                            </Card.Title>

                                            <Card.Text>
                                                {t('starting_from_price', {price: formatMoney(model.price)})}
                                            </Card.Text>

                                            <Card.Text>
                                                {model.size.label} <span>{model.size.value}</span>
                                            </Card.Text>

                                            <Card.Text>
                                                {t('Life expectancy:')} {t('+years', {years: model.lifeTime})}
                                            </Card.Text>

                                        </Card.Body>
                                        <Card.Img variant="bottom" src={model.img} alt={model.name} />

                                    </Card>
                                </Col>
                            )
                        })}

                    </Row>
                </Col>
                <Col xs={12}>
                    <h3 className='mt-3 mb-3'>
                        {t('Reservation fee due today')}: {formatMoney(checkout.total.total.minorUnitsAmount / 100)}
                    </h3>
                    <form id="payment-form" onSubmit={handleSubmit}>
                        <Card>
                            <Card.Body>
                                <Card.Title>{t('_checkout.contact_info_title')}</Card.Title>
                                <Row>
                                    <Col xs={12} lg={6} className="mb-4 mb-lg-0">

                                        <Form.Label htmlFor="name">{t('name')}</Form.Label>
                                        <Form.Control
                                            id="name"
                                            type="text"
                                            value={contact.name}
                                            onChange={(e) => setContact({...contact, name: e.target.value})}
                                            required={true}
                                            onBlur={() => handleUpdateContact()}
                                            isInvalid={!contact.name || contact.name === ''}
                                        />
                                        <Form.Text id="nameHelpBlock" muted>

                                        </Form.Text>
                                    </Col>


                                    <Col xs={12} lg={6} className="mb-4 mb-lg-0">

                                        <Form.Label htmlFor="email">{t('email_address')}</Form.Label>
                                        <Form.Control
                                            id="email"
                                            type="email"
                                            value={contact.email}
                                            required={true}
                                            onChange={(e) => setContact({...contact, email: e.target.value})}
                                            onBlur={() => handleUpdateContact()}
                                            isInvalid={!validateEmail(contact.email)}
                                        />
                                        <Form.Text id="emailHelpBlock" muted>
                                            {t('We will send reservation updates to this email.')}
                                        </Form.Text>
                                    </Col>

                                    <Col xs={12} lg={6} className="mb-4 mb-lg-0">

                                        <Form.Label htmlFor="phone">{t('phone_number')}</Form.Label>

                                        <PhoneInput
                                            defaultCountry="ca"
                                            value={contact.phone}
                                            required={true}
                                            onChange={(phone) => setContact({...contact, phone: phone})}
                                            inputClassName={`form-control ps-5 ${(contact.phone) && !isPhoneValid() ? 'is-invalid' : ''}`}
                                            inputStyle={{
                                                height: '38px',
                                                fontSize: '1rem',
                                                borderRadius: 'var(--bs-border-radius)'
                                            }}
                                            placeholder=''
                                            preferredCountries={['ca']}
                                            countrySelectorStyleProps={{
                                                buttonClassName: 'm-0 p-0',
                                                buttonStyle: {
                                                    height: '35px',
                                                    position: 'absolute',
                                                    border: 'none',
                                                    left: '14px',
                                                    top: '2px'
                                                },
                                                flagStyle: {height: '16px'}

                                            }}
                                            onBlur={() => handleUpdateContact()}
                                            forceDialCode={true}
                                            dialCodePreviewStyleProps={{
                                                style: {border: '21px solid red', display: 'none'}
                                            }}
                                        />

                                        <Form.Text id="phoneHelpBlock" muted>

                                        </Form.Text>

                                    </Col>


                                    <Col xs={12} lg={6} className="mb-4 mb-lg-0">

                                        <Form.Label htmlFor="company">{t('company')} ({t('optional')})</Form.Label>
                                        <Form.Control
                                            id="company"
                                            type="text"
                                            value={contact.company}
                                            onChange={(e) => setContact({...contact, company: e.target.value})}
                                            onBlur={() => handleUpdateContact()}
                                            isInvalid={contact.company !== undefined && !isCompanyValid(contact.company)}
                                        />
                                        <Form.Text id="companyHelpBlock" muted>

                                        </Form.Text>
                                    </Col>


                                </Row>

                            </Card.Body>
                        </Card>

                        <Card className="mt-4">
                            <Card.Body>
                                <Card.Title>{t('_checkout.payment_options_title')}</Card.Title>
                                <Row>
                                    <Col xs={12}>
                                        <PaymentElement id="payment-element" options={paymentElementOptions}/>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <div className="mt-2">
                            <Button variant="primary d-flex justify-content-center ms-auto" disabled={isReserveButtonDisabled()}
                                    type="submit">
                                {isLoading ?
                                    <>
                                        <Spinner animation="border" size="sm" className="p-2"/>
                                        <span style={{opacity: 0}}>.</span>
                                    </>
                                    :
                                    t('Reserve')
                                }
                            </Button>

                            {/* Show any error or success messages */}
                            {message &&
                                <Alert variant={'danger'} className='d-flex gap-2 align-items-center mt-2' dismissible>
                                    {message}
                                </Alert>
                            }
                        </div>


                    </form>
                </Col>

                <Col xs={12} className="mt-4 reservationNotesContainer">

                    <Trans i18nKey="terms_message">
                        <a href={`${langPrefix}/${t('_route.reservation-agreement')}`} rel="noreferrer"  target="_blank"> </a>

                        <a href={`${langPrefix}/${t('_route.terms-of-service')}`} rel="noreferrer"  target="_blank"> </a>

                        <a href={`${langPrefix}/${t('_route.privacy-policy')}`} rel="noreferrer"  target="_blank"> </a>

                    </Trans>

                    <ol className='ps-3'>
                        <li>
                            <strong>{t('Pricing applies to the Canadian market only.')}</strong>&nbsp;
                            {t('The listed price reflects the Manufacturer Suggested Retail Price (MSRP) and does not include taxes, permits, site prep, utility connections, delivery, installation or optional upgrades and premium features.')}
                        </li>

                        <li>
                            <strong>{t('Production of PakVille homes is underway.')}</strong>&nbsp;
                            {t('Availability and delivery timelines may vary depending on your model, location, and local permitting processes.')}
                        </li>
                    </ol>
                </Col>
            </Row>
        </Container>

    </>)
}

export default ReserveCheckoutFormComponent;